// Enhanced utility: tries preferred orientation, then flips if needed, then clamps as last resort
export function getOnboardingTooltipStyle({
    rect,
    orientation,
    tooltipWidth,
    tooltipHeight,
    viewportWidth,
    viewportHeight
}) {
    // Only flip if needed: try preferred, then opposite, then clamp
    const padding = 10
    const style = {}

    // Calculate initial position for each orientation
    function getPosition(o) {
        let top, left
        if (o === 'right') {
            top = rect.top + rect.height / 2 - tooltipHeight / 2
            left = rect.right + padding
        } else if (o === 'bottom') {
            top = rect.bottom + padding
            left = rect.left + rect.width / 2 - tooltipWidth / 2
        } else if (o === 'left') {
            top = rect.top + rect.height / 2 - tooltipHeight / 2
            left = rect.left - padding - tooltipWidth
        } else if (o === 'top') {
            top = rect.top - padding - tooltipHeight
            left = rect.left + rect.width / 2 - tooltipWidth / 2
        }
        return { top, left }
    }

    // Clamp a value between min and max
    function clamp(val, min, max) {
        return Math.max(min, Math.min(val, max))
    }

    // Try preferred orientation first
    let { top, left } = getPosition(orientation)
    let fits = true
    let finalOrientation = orientation
    let arrowOffset = 'center'

    // Helper to determine arrow offset for top/bottom (left/center/right) or left/right (top/center/bottom)
    function getArrowOffset(orientation, left, top, idealLeft, idealTop) {
        const threshold = 32 // px, how far from center before we call it left/right
        if (orientation === 'top' || orientation === 'bottom') {
            if (Math.abs(left - idealLeft) < threshold) return 'center'
            if (left > idealLeft) return 'left' // shifted right, arrow on left
            if (left < idealLeft) return 'right' // shifted left, arrow on right
        } else {
            if (Math.abs(top - idealTop) < threshold) return 'center'
            if (top > idealTop) return 'top' // shifted down, arrow on top
            if (top < idealTop) return 'bottom' // shifted up, arrow on bottom
        }
        return 'center'
    }

    // Save the ideal position for arrow offset calculation
    const { top: idealTop, left: idealLeft } = getPosition(orientation)

    // Check if it fits vertically (for top/bottom) or horizontally (for left/right)
    if (orientation === 'bottom' || orientation === 'top') {
        if (left < padding) left = padding
        if (left + tooltipWidth > viewportWidth - padding) left = viewportWidth - tooltipWidth - padding
        if (top < padding || top + tooltipHeight > viewportHeight - padding) fits = false
    } else {
        if (top < padding) top = padding
        if (top + tooltipHeight > viewportHeight - padding) top = viewportHeight - tooltipHeight - padding
        if (left < padding || left + tooltipWidth > viewportWidth - padding) fits = false
    }
    arrowOffset = getArrowOffset(orientation, left, top, idealLeft, idealTop)

    // If it doesn't fit, try the opposite orientation
    if (!fits) {
        const opposite =
            orientation === 'bottom'
                ? 'top'
                : orientation === 'top'
                  ? 'bottom'
                  : orientation === 'left'
                    ? 'right'
                    : 'left'
        ;({ top, left } = getPosition(opposite))
        finalOrientation = opposite
        const { top: idealTop2, left: idealLeft2 } = getPosition(opposite)
        fits = true
        if (opposite === 'bottom' || opposite === 'top') {
            if (left < padding) left = padding
            if (left + tooltipWidth > viewportWidth - padding) left = viewportWidth - tooltipWidth - padding
            if (top < padding || top + tooltipHeight > viewportHeight - padding) fits = false
        } else {
            if (top < padding) top = padding
            if (top + tooltipHeight > viewportHeight - padding) top = viewportHeight - tooltipHeight - padding
            if (left < padding || left + tooltipWidth > viewportWidth - padding) fits = false
        }
        arrowOffset = getArrowOffset(finalOrientation, left, top, idealLeft2, idealTop2)
    }

    // If still doesn't fit, clamp to viewport
    if (!fits) {
        top = clamp(top, padding, viewportHeight - tooltipHeight - padding)
        left = clamp(left, padding, viewportWidth - tooltipWidth - padding)
        // Recalculate arrowOffset after clamp
        arrowOffset = 'center' // fallback, since clamped
    }

    if (typeof top === 'number') style.top = `${top}px`
    if (typeof left === 'number') style.left = `${left}px`
    return { style, orientation: finalOrientation, arrowOffset }
}
